import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { projects } from "@/lib/projects";

// Single paw print — used to build a directional trail that leads the eye
// from the title toward the CTA arrow. The cat metaphor becomes intentional:
// Michi guides you to the project.
const Paw = ({ className = "", opacity = 0.9 }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" className={className} aria-hidden="true">
    <g fill="currentColor" opacity={opacity}>
      <ellipse cx="5" cy="7" rx="1.3" ry="1.9" />
      <ellipse cx="10" cy="5" rx="1.3" ry="1.9" />
      <ellipse cx="15" cy="7" rx="1.3" ry="1.9" />
      <ellipse cx="12" cy="11" rx="1.3" ry="1.9" />
      <path d="M7 16 C 5 13, 15 13, 13 16 C 12 18, 8 18, 7 16 Z" />
    </g>
  </svg>
);

function Card({ p, i }) {
  const cover = p.gallery?.[0];
  const accent = p.accent || "#FF6B2C";
  const accentWord =
    p.n === "001" ? "proximidad." : p.n === "002" ? "serenidad." : "sensibilidad.";

  return (
    <motion.article
      data-testid={`work-tile-${i + 1}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: i * 0.08 }}
      className="group"
    >
      <Link
        to={`/proyectos/${p.slug}`}
        className="block"
        data-cursor="image"
        data-label="Descubrir"
        aria-label={`Descubrir proyecto: ${p.card}`}
      >
        {/* Cover — smaller, editorial */}
        <div
          className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden border border-[color:var(--ink-10)]"
          style={{ background: "#F2EFEB" }}
        >
          {/* Cover image */}
          <motion.img
            src={cover}
            alt={p.card}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-[1.05]"
            initial={{ scale: 1.05 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1] }}
            loading="lazy"
          />

          {/* Gradient for legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />

          {/* Grain */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-multiply"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
            }}
          />

          {/* Corner ticks — editorial framing */}
          <div className="absolute top-3 left-3 w-4 h-4 border-l border-t border-white/60" />
          <div className="absolute top-3 right-3 w-4 h-4 border-r border-t border-white/60" />
          <div className="absolute bottom-3 left-3 w-4 h-4 border-l border-b border-white/60" />
          <div className="absolute bottom-3 right-3 w-4 h-4 border-r border-b border-white/60" />

          {/* Top meta row */}
          <div className="absolute top-5 left-5 right-5 flex items-center justify-between font-sans text-[10px] tracking-[0.22em] uppercase text-white/85">
            <span>N.º {p.n}</span>
            <span className="opacity-80">{p.year}</span>
          </div>

          {/* Bottom title + accent word + guided paw trail */}
          <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6">
            <div
              className="font-italic-display text-base leading-tight mb-1.5"
              style={{ color: accent }}
            >
              {accentWord}
            </div>
            <h3 className="font-display text-white text-xl lg:text-[1.6rem] leading-[1.05] tighter">
              {p.card}
            </h3>

            {/* CTA row with intentional paw trail */}
            <div className="mt-4 flex items-center justify-between">
              <div
                className="font-sans text-[10px] tracking-[0.24em] uppercase text-white/75"
              >
                Descubrir
              </div>

              {/* Paw trail — leads the eye from title to arrow. Paws light up on hover in a directional cascade. */}
              <div className="flex items-center gap-2 text-white">
                <span
                  className="opacity-0 -translate-x-2 group-hover:opacity-70 group-hover:translate-x-0 transition-all duration-500"
                  style={{ transitionDelay: "80ms" }}
                >
                  <Paw />
                </span>
                <span
                  className="opacity-0 -translate-x-2 group-hover:opacity-90 group-hover:translate-x-0 transition-all duration-500"
                  style={{ transitionDelay: "200ms" }}
                >
                  <Paw />
                </span>
                <span
                  className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500"
                  style={{ transitionDelay: "320ms" }}
                >
                  <Paw />
                </span>

                {/* Arrow — the destination */}
                <span
                  className="ml-1 inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/70 text-white group-hover:border-white transition-all duration-500 group-hover:translate-x-1"
                  style={{ transitionDelay: "0ms" }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    aria-hidden="true"
                    className="transition-transform duration-500 group-hover:rotate-[-45deg]"
                  >
                    <path
                      d="M2 6 L 10 6 M 6 2 L 10 6 L 6 10"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Editorial meta row below card */}
        <div className="mt-4 pt-4 border-t border-[color:var(--ink-10)] flex items-start justify-between gap-4 font-sans">
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] tracking-[0.16em] uppercase text-[color:var(--ink)]/55">
            {p.disciplines?.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <span className="shrink-0 text-[10px] tracking-[0.18em] uppercase text-[color:var(--ink)]/45">
            Cap. {p.n}
          </span>
        </div>
      </Link>
    </motion.article>
  );
}

export default function SelectedWork() {
  return (
    <section id="work" data-testid="selected-work" className="relative pt-12 lg:pt-16 pb-16 lg:pb-20">
      <div className="max-w-[95%] mx-auto">
        <div className="flex items-end justify-between mb-12 lg:mb-16 gap-8">
          <div>
            <div className="font-sans text-[11px] tracking-[0.24em] uppercase text-[color:var(--ink-60)] mb-6">
              — Destinos · Índice 004
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tightest max-w-4xl pb-2">
              Tres sectores,
              <br />
              <span className="font-italic-display">un mismo criterio.</span>
            </h2>
          </div>
          <div className="hidden lg:block max-w-xs">
            <p className="font-sans text-sm leading-relaxed text-[color:var(--ink-60)]">
              Cada destino demuestra cómo el estudio aborda un sector distinto. No es un portfolio: son conceptos editoriales.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {projects.map((p, i) => (
            <Card p={p} i={i} key={p.slug} />
          ))}
        </div>

        <p className="mt-12 font-sans text-[13px] leading-relaxed text-[color:var(--ink-60)] max-w-2xl">
          Conceptos desarrollados por La Michi Agencia para mostrar nuestro enfoque creativo.
        </p>
      </div>
    </section>
  );
}
